/**
 * StageCompleteModalV2 - Stage completion modal
 *
 * Features:
 * - Shows stage completion stats
 * - Celebratory animation
 * - Next stage or finish challenge button
 */

import { memo, useCallback, useEffect } from 'react'
import {
  useChallengeV2 as useChallengeStoreV2,
  selectIsLastStage
} from '../../../hooks/useChallenge'

// Celebration icon
const CelebrationIcon = () => (
  <svg className="w-16 h-16" viewBox="0 0 64 64" fill="none">
    <circle cx="32" cy="32" r="28" className="fill-emerald-100 dark:fill-emerald-900/30" />
    <path
      d="M20 32l8 8 16-16"
      className="stroke-emerald-500"
      strokeWidth="4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const StageCompleteModalV2 = memo(() => {
  // Store state
  const showStageCompleteModal = useChallengeStoreV2((state) => state.showStageCompleteModal)
  const isLastStage = useChallengeStoreV2(selectIsLastStage)
  const stages = useChallengeStoreV2((state) => state.stages)
  const currentStageIndex = useChallengeStoreV2((state) => state.currentStageIndex)

  // Store actions
  const closeStageCompleteModal = useChallengeStoreV2((state) => state.closeStageCompleteModal)
  const handleNextStage = useChallengeStoreV2((state) => state.handleNextStage)

  // Handle continue
  const handleContinue = useCallback(() => {
    handleNextStage()
  }, [handleNextStage])

  // Prevent body scroll
  useEffect(() => {
    if (showStageCompleteModal) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [showStageCompleteModal])

  if (!showStageCompleteModal) return null

  const nextStage = stages[currentStageIndex + 1]

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

      {/* Modal */}
      <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full p-8 text-center animate-[modalIn_0.3s_ease-out]">
        {/* Celebration animation */}
        <div className="flex justify-center mb-6 animate-bounce">
          <CelebrationIcon />
        </div>

        {/* Title */}
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Stage Complete!</h2>

        <p className="text-gray-600 dark:text-gray-400 mb-6">
          Great work! You've completed this stage.
        </p>

        {/* Next stage info */}
        {!isLastStage && nextStage && (
          <div className="mb-6 p-4 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg">
            <p className="text-sm text-indigo-700 dark:text-indigo-300">
              Next:{' '}
              <span className="font-medium">
                {nextStage.name || `Stage ${currentStageIndex + 2}`}
              </span>
            </p>
          </div>
        )}

        {/* Action buttons */}
        <div className="flex gap-3">
          <button
            onClick={handleContinue}
            className="flex-1 px-6 py-3 bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white font-semibold rounded-xl shadow-md hover:shadow-lg transition-all duration-200"
          >
            {isLastStage ? 'View Results' : 'Continue to Next Stage'}
          </button>
        </div>
      </div>
    </div>
  )
})

StageCompleteModalV2.displayName = 'StageCompleteModalV2'

export default StageCompleteModalV2
